import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { publicApi, formatApiError } from '../lib/api';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const COUNTRY_CENTER = { quebec: [46.81, -71.21], senegal: [14.5, -14.45] };

function compressImage(file, maxDim = 1000, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const img = new Image();
    reader.onload = (e) => { img.src = e.target.result; };
    reader.onerror = reject;
    img.onload = () => {
      let { width, height } = img;
      if (width > height && width > maxDim) { height = (height * maxDim) / width; width = maxDim; }
      else if (height > maxDim) { width = (width * maxDim) / height; height = maxDim; }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function ClickPicker({ onPick }) {
  useMapEvents({ click(e) { onPick(e.latlng.lat, e.latlng.lng); } });
  return null;
}
function Recenter({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView(center, 15); }, [center, map]);
  return null;
}

export default function Signaler() {
  const { communeId } = useParams();
  const [commune, setCommune] = useState(null);
  const [communes, setCommunes] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pos, setPos] = useState(null); // [lat, lng]
  const [form, setForm] = useState({
    commune_id: communeId || '', type: '', description: '',
    citizen_name: '', citizen_contact: '', photo: null, website: '',
  });

  // Charge la commune (lien dédié) OU la liste (lien global)
  useEffect(() => {
    (async () => {
      try {
        if (communeId) {
          const { data } = await publicApi.commune(communeId);
          setCommune(data);
          const { data: ts } = await publicApi.infraTypes(data.country_mode);
          setTypes(ts || []);
        } else {
          const { data } = await publicApi.communes();
          setCommunes(data || []);
        }
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [communeId]);

  // Lien global : quand le citoyen choisit une commune, charge les types
  const onCommunePicked = async (id) => {
    setForm((f) => ({ ...f, commune_id: id }));
    const c = communes.find((x) => x.id === id);
    setCommune(c || null);
    if (c) {
      try { const { data } = await publicApi.infraTypes(c.country_mode); setTypes(data || []); } catch {}
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { setError("La géolocalisation n'est pas disponible sur cet appareil."); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => setPos([p.coords.latitude, p.coords.longitude]),
      () => setError("Impossible d'obtenir votre position. Placez le point manuellement sur la carte."),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await compressImage(file);
      setForm((f) => ({ ...f, photo: dataUrl }));
    } catch { setError("Impossible de traiter la photo."); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.commune_id) { setError('Veuillez sélectionner votre commune.'); return; }
    if (!form.type) { setError('Veuillez choisir le type de problème.'); return; }
    if (!form.description.trim()) { setError('Veuillez décrire le problème.'); return; }
    if (!pos) { setError('Veuillez indiquer la localisation (bouton « Ma position » ou clic sur la carte).'); return; }
    setSubmitting(true);
    try {
      await publicApi.createSignalement({
        commune_id: form.commune_id,
        type: form.type,
        titre: form.description.trim().slice(0, 80),
        description: form.description.trim(),
        latitude: pos[0],
        longitude: pos[1],
        photo: form.photo,
        citizen_name: form.citizen_name,
        citizen_contact: form.citizen_contact,
        website: form.website,
      });
      setDone(true);
    } catch (ex) {
      setError(formatApiError(ex));
    } finally {
      setSubmitting(false);
    }
  };

  const center = pos || (commune ? COUNTRY_CENTER[commune.country_mode] : COUNTRY_CENTER.quebec);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Chargement…</div>;
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F6F9] p-6" data-testid="signaler-done">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 max-w-md text-center">
          <div className="text-5xl mb-3">✅</div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Merci !</h1>
          <p className="text-sm text-gray-600">Votre signalement a bien été transmis{commune?.nom ? ` à ${commune.nom}` : ''}. Les services municipaux vont l'examiner.</p>
          <button onClick={() => { setDone(false); setPos(null); setForm({ commune_id: communeId || '', type: '', description: '', citizen_name: '', citizen_contact: '', photo: null, website: '' }); }} className="mt-6 px-5 py-2.5 rounded-lg bg-[#002855] text-white text-sm font-semibold" data-testid="signaler-new">
            Signaler un autre problème
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F6F9]" data-testid="signaler-page">
      <header className="bg-[#002855] text-white px-5 py-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-[11px] uppercase tracking-widest text-white/60">Signalement citoyen</div>
          <h1 className="text-lg font-semibold">{commune?.tenant_nom || commune?.nom || 'GeoDash'}</h1>
          <p className="text-xs text-white/70 mt-0.5">Aidez votre commune en signalant un problème d'infrastructure.</p>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-5">
        <form onSubmit={submit} className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
          {/* Honeypot anti-spam (caché) */}
          <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} tabIndex={-1} autoComplete="off" style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }} aria-hidden="true" />

          {!communeId && (
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Votre commune *</label>
              <select value={form.commune_id} onChange={(e) => onCommunePicked(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm" data-testid="signaler-commune">
                <option value="">— Choisir —</option>
                {communes.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Type de problème *</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm" data-testid="signaler-type" disabled={!types.length}>
              <option value="">— Choisir —</option>
              {types.map((t) => <option key={t.id} value={t.code}>{t.icone ? `${t.icone} ` : ''}{t.nom}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Description *</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Décrivez le problème (ex. lampadaire cassé, fuite d'eau, nid-de-poule…)" className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm outline-none focus:border-blue-600" data-testid="signaler-description" />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Localisation *</label>
            <button type="button" onClick={useMyLocation} className="mb-2 text-xs px-3 py-2 rounded-lg bg-blue-50 text-blue-800 font-semibold" data-testid="signaler-geoloc">📍 Utiliser ma position</button>
            <div className="rounded-lg overflow-hidden border border-gray-200" style={{ height: 220 }}>
              <MapContainer center={center} zoom={pos ? 15 : 11} style={{ height: '100%', width: '100%' }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                <ClickPicker onPick={(lat, lng) => setPos([lat, lng])} />
                {pos && <Marker position={pos} />}
                {pos && <Recenter center={pos} />}
              </MapContainer>
            </div>
            <p className="text-[11px] text-gray-400 mt-1">{pos ? `Position : ${pos[0].toFixed(5)}, ${pos[1].toFixed(5)}` : 'Cliquez sur la carte ou utilisez votre position.'}</p>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Photo (optionnelle)</label>
            <input type="file" accept="image/*" capture="environment" onChange={onPhoto} className="text-sm" data-testid="signaler-photo" />
            {form.photo && <img src={form.photo} alt="aperçu" className="mt-2 rounded-lg max-h-40 border border-gray-200" />}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Votre nom (facultatif)</label>
              <input type="text" value={form.citizen_name} onChange={(e) => setForm({ ...form, citizen_name: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm" data-testid="signaler-name" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Tél / email (facultatif)</label>
              <input type="text" value={form.citizen_contact} onChange={(e) => setForm({ ...form, citizen_contact: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm" data-testid="signaler-contact" />
            </div>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm" data-testid="signaler-error">⚠️ {error}</div>}

          <button type="submit" disabled={submitting} className="w-full px-5 py-3 rounded-lg bg-[#002855] text-white font-semibold text-sm disabled:opacity-50" data-testid="signaler-submit">
            {submitting ? 'Envoi…' : 'Envoyer mon signalement'}
          </button>
          <p className="text-[11px] text-gray-400 text-center">Aucun compte requis. Vos données ne servent qu'au traitement de votre signalement.</p>
        </form>
      </main>
    </div>
  );
}
